import React, { useState, useEffect } from 'react';
import Input from './ui/Input';
import Icon from './AppIcon';
import { US_STATES } from '../lib/us-states';
import { CA_PROVINCES } from '../lib/ca-provinces';
import { NA_PHONE_COUNTRIES, normalizeNaPhoneCountry } from '../lib/na-phone-countries';

/**
 * Shared area code search UI — manual entry first, then country/region browse with Twilio availability.
 *
 * @param {object} props
 * @param {string} props.value - Selected 3-digit area code
 * @param {(code: string) => void} props.onChange
 * @param {string} props.country - 'US' or 'CA'
 * @param {(country: 'US' | 'CA') => void} props.onCountryChange
 * @param {() => Promise<Record<string, string>>} props.getHeaders - Auth headers for API calls
 * @param {boolean} [props.showCountrySelector=true]
 * @param {boolean} [props.showUnavailableHint=true]
 * @param {string} [props.unavailableHint]
 * @param {(status: { checkingAvailability: boolean, twilioAvailable: boolean|null, alternatives: string[] }) => void} [props.onStatusChange]
 */
const AreaCodePicker = ({
  value,
  onChange,
  country,
  onCountryChange,
  getHeaders,
  showCountrySelector = true,
  showUnavailableHint = true,
  unavailableHint,
  onStatusChange,
}) => {
  const naCountry = normalizeNaPhoneCountry(country);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [loadingRegionCodes, setLoadingRegionCodes] = useState(false);
  const [regionAreaCodes, setRegionAreaCodes] = useState([]);
  const [regionSearchError, setRegionSearchError] = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [twilioAvailable, setTwilioAvailable] = useState(null);
  const [alternatives, setAlternatives] = useState([]);

  const regions = naCountry === 'CA' ? CA_PROVINCES : US_STATES;
  const regionLabel = naCountry === 'CA' ? 'Your Province' : 'Your State';
  const regionSelectPlaceholder = naCountry === 'CA' ? '— Select a province —' : '— Select a state —';
  const neighbourLabel = naCountry === 'CA' ? 'province' : 'state';
  const areaCodePlaceholder = naCountry === 'CA' ? '416' : '310';

  useEffect(() => {
    onStatusChange?.({ checkingAvailability, twilioAvailable, alternatives });
  }, [checkingAvailability, twilioAvailable, alternatives, onStatusChange]);

  useEffect(() => {
    if (value.length !== 3) {
      setTwilioAvailable(null);
      setAlternatives([]);
      setCheckingAvailability(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setCheckingAvailability(true);
      setTwilioAvailable(null);
      setAlternatives([]);
      try {
        const headers = await getHeaders();
        const response = await fetch('/api/agents/check-area-code', {
          method: 'POST',
          headers,
          body: JSON.stringify({ areaCode: value, country: naCountry }),
        });
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok) {
          setTwilioAvailable(false);
          setAlternatives(data.alternatives || []);
          return;
        }
        setTwilioAvailable(data.available === true);
        setAlternatives(data.alternatives || []);
      } catch {
        if (!cancelled) {
          setTwilioAvailable(false);
          setAlternatives([]);
        }
      } finally {
        if (!cancelled) setCheckingAvailability(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [value, naCountry]); // getHeaders intentionally omitted — stable via useCallback in callers

  const handleCountryChange = (e) => {
    const next = normalizeNaPhoneCountry(e.target.value);
    onCountryChange(next);
    setSelectedRegion('');
    setRegionAreaCodes([]);
    setRegionSearchError('');
    onChange('');
    setTwilioAvailable(null);
    setAlternatives([]);
  };

  const handleAreaCodeChange = (e) => {
    onChange(e.target.value.replace(/\D/g, '').slice(0, 3));
  };

  const handleRegionChange = async (e) => {
    const region = e.target.value;
    setSelectedRegion(region);
    setRegionAreaCodes([]);
    setRegionSearchError('');
    onChange('');
    setTwilioAvailable(null);
    setAlternatives([]);
    if (!region) return;

    setLoadingRegionCodes(true);
    try {
      const headers = await getHeaders();
      const response = await fetch('/api/agents/list-area-codes', {
        method: 'POST',
        headers,
        body: JSON.stringify({ region, country: naCountry }),
      });
      const data = await response.json();
      if (!response.ok) {
        setRegionSearchError(data.error || `Failed to load area codes for this ${neighbourLabel}.`);
      } else {
        setRegionAreaCodes(data.areaCodes || []);
        if ((data.areaCodes || []).length === 0) {
          setRegionSearchError(
            `No SMS numbers currently available in this ${neighbourLabel}. Try a neighbouring ${neighbourLabel} or enter a code manually above.`
          );
        }
      }
    } catch {
      setRegionSearchError(`Could not load area codes. Please try again or enter a code manually above.`);
    } finally {
      setLoadingRegionCodes(false);
    }
  };

  const regionName = regions.find((r) => r.value === selectedRegion)?.label;

  return (
    <div>
      <div className="mb-2 flex flex-col items-start sm:items-center">
        <label className="block text-sm font-medium text-foreground mb-2">
          Enter Area Code
        </label>
        <Input
          type="text"
          placeholder={areaCodePlaceholder}
          value={value}
          onChange={handleAreaCodeChange}
          maxLength={3}
          className="w-32 text-center text-2xl font-bold"
        />
        {value.length === 3 && (
          <div className="mt-3 text-center space-y-2 w-full">
            {checkingAvailability && (
              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                <Icon name="Loader2" size={14} className="animate-spin" />
                Checking availability…
              </p>
            )}
            {!checkingAvailability && twilioAvailable === true && (
              <p className="text-xs text-green-600 flex items-center justify-center gap-1">
                <Icon name="CheckCircle" size={14} />
                Area code {value} is available in {naCountry === 'CA' ? 'Canada' : 'the US'}
              </p>
            )}
            {!checkingAvailability && twilioAvailable === false && (
              <div className="text-xs text-amber-700 space-y-2">
                <p className="flex items-center justify-center gap-1">
                  <Icon name="AlertTriangle" size={14} />
                  No exact match for {value} right now
                </p>
                {alternatives.length > 0 && (
                  <div>
                    <p className="text-muted-foreground">Try a nearby code:</p>
                    <div className="flex flex-wrap justify-center gap-2 mt-1">
                      {alternatives.map((code) => (
                        <button
                          key={code}
                          type="button"
                          onClick={() => onChange(code)}
                          className="px-3 py-1 rounded-md border border-primary text-primary text-sm font-medium hover:bg-primary/10"
                        >
                          {code}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {showUnavailableHint && twilioAvailable === false && value.length === 3 && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-medium mb-1">No exact numbers in area code {value}</p>
          <p>
            {unavailableHint || 'Pick an alternative above, or browse by region below.'}
          </p>
        </div>
      )}

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" />
        <span className="text-xs text-muted-foreground">
          or select a {neighbourLabel}
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>

      {showCountrySelector && (
        <div className="mb-5">
          <label className="block text-sm font-medium text-foreground mb-1.5">
            Country
          </label>
          <select
            value={naCountry}
            onChange={handleCountryChange}
            className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            {NA_PHONE_COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      )}

      <div className="mb-5">
        <label className="block text-sm font-medium text-foreground mb-1.5">
          {regionLabel}
        </label>
        <select
          value={selectedRegion}
          onChange={handleRegionChange}
          className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
        >
          <option value="">{regionSelectPlaceholder}</option>
          {regions.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      {selectedRegion && (
        <div className="mb-2">
          {loadingRegionCodes && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5 mb-2">
              <Icon name="Loader2" size={13} className="animate-spin" />
              Loading available area codes for {regionName}…
            </p>
          )}
          {!loadingRegionCodes && regionSearchError && (
            <p className="text-xs text-destructive mb-2">{regionSearchError}</p>
          )}
          {!loadingRegionCodes && regionAreaCodes.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-2">
                Available area codes in {regionName} — click to select:
              </p>
              <div className="flex flex-wrap gap-2">
                {regionAreaCodes.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => onChange(code)}
                    className={`px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                      value === code
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border text-foreground hover:border-primary hover:text-primary hover:bg-primary/5'
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AreaCodePicker;
